/**
 * Smart link prefetching for likely next pages
 * Prefetches on hover, caches for reuse
 */

(function() {
  'use strict';

  const HOSTNAME = window.location.hostname || '';
  const SEARCH_PARAMS = new URLSearchParams(window.location.search);
  const IS_LOCALHOST = ['localhost', '127.0.0.1', '::1'].includes(HOSTNAME) || HOSTNAME.endsWith('.local');
  const IS_THEME_PREVIEW = SEARCH_PARAMS.has('preview_theme_id');

  if (IS_LOCALHOST || IS_THEME_PREVIEW) {
    return;
  }

  const PREFETCH_DELAY = 40; // ms delay before prefetching
  const LINK_SELECTOR = 'a[href^="/"]:not([data-prefetch="false"])';
  const PREFETCHED = new Set();
  const PREFETCH_TIMEOUTS = new WeakMap();
  const PREFETCHED_MENUS = new Set();
  const CURRENT_PATH = window.location.pathname + window.location.search;

  function normalizeUrl(url) {
    if (!url) return null;
    try {
      const parsed = new URL(url, window.location.origin);
      parsed.hash = '';
      return parsed.pathname + parsed.search;
    } catch (err) {
      return url.replace(/#.*/, '') || null;
    }
  }

  function canPrefetch(normalizedUrl) {
    if (!normalizedUrl) return false;
    if (normalizedUrl === CURRENT_PATH) return false;
    return !PREFETCHED.has(normalizedUrl);
  }

  function prefetch(url) {
    const normalized = normalizeUrl(url);
    if (!canPrefetch(normalized)) return;

    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    document.head.appendChild(link);

    PREFETCHED.add(normalized);
  }

  function cancelPrefetch(linkEl) {
    const timeoutId = PREFETCH_TIMEOUTS.get(linkEl);
    if (!timeoutId) return;
    clearTimeout(timeoutId);
    PREFETCH_TIMEOUTS.delete(linkEl);
  }

  function schedulePrefetch(linkEl) {
    if (!linkEl) return;
    const href = linkEl.getAttribute('href');
    const normalized = normalizeUrl(href);
    if (!canPrefetch(normalized)) return;

    cancelPrefetch(linkEl);

    const timeoutId = setTimeout(() => {
      prefetch(href);
      PREFETCH_TIMEOUTS.delete(linkEl);
    }, PREFETCH_DELAY);

    PREFETCH_TIMEOUTS.set(linkEl, timeoutId);
  }

  function extractLinkFromMouseEvent(event) {
    const targetLink = event.target?.closest?.(LINK_SELECTOR);
    if (!targetLink) return null;
    if (targetLink.contains(event.relatedTarget)) return null;
    return targetLink;
  }

  function prefetchMegaMenu(handle) {
    if (!handle || PREFETCHED_MENUS.has(handle)) return;

    const template = document.getElementById(`submenu-${handle}`);
    if (template instanceof HTMLTemplateElement) {
      template.content.querySelectorAll(LINK_SELECTOR).forEach(link => {
        const href = link.getAttribute('href');
        if (href) prefetch(href);
      });
    }

    PREFETCHED_MENUS.add(handle);
  }

  function handlePotentialMegaMenuTarget(target, relatedTarget) {
    const trigger = target?.closest?.('[data-menu-handle]');
    if (!trigger) return;
    if (relatedTarget && trigger.contains(relatedTarget)) return;

    const menuHandle = trigger.getAttribute('data-menu-handle');
    if (menuHandle) {
      prefetchMegaMenu(menuHandle);
    }
  }

  document.addEventListener('mouseover', event => {
    handlePotentialMegaMenuTarget(event.target, event.relatedTarget);

    const linkEl = extractLinkFromMouseEvent(event);
    if (linkEl) {
      schedulePrefetch(linkEl);
    }
  }, { passive: true });

  document.addEventListener('mouseout', event => {
    const linkEl = extractLinkFromMouseEvent(event);
    if (linkEl) {
      cancelPrefetch(linkEl);
    }
  }, { passive: true });

  document.addEventListener('focusin', event => {
    handlePotentialMegaMenuTarget(event.target, null);

    const linkEl = event.target?.closest?.(LINK_SELECTOR);
    if (linkEl) {
      schedulePrefetch(linkEl);
    }
  });

  document.addEventListener('touchstart', event => {
    const linkEl = event.target?.closest?.(LINK_SELECTOR);
    if (linkEl) {
      cancelPrefetch(linkEl);
      const href = linkEl.getAttribute('href');
      if (href) prefetch(href);
    }
  }, { passive: true });

  // Prefetch visible nav links on idle (primary navigation)
  const navLinks = document.querySelectorAll('nav a, header a');
  if (navLinks.length > 0 && 'requestIdleCallback' in window) {
    requestIdleCallback(() => {
      Array.from(navLinks)
        .filter(link => link.matches?.(LINK_SELECTOR))
        .slice(0, 6)
        .forEach(link => {
          const href = link.getAttribute('href');
          if (href) prefetch(href);
        });
    }, { timeout: 2000 });
  }
})();
